import { all, call, put, takeLatest, select } from 'redux-saga/effects';
import jwtDecode from 'jwt-decode';
import {
  FETCH_ROLES,
  RECEIVE_ROLES,
  FETCH_ROLES_FAILURE,
  FETCH_PERMISSIONS,
  RECEIVE_PERMISSIONS
} from 'src/store/actions/accountActions';
import { PROVIDERS_SUCCESS } from 'src/store/actions/providerActions';
import { PARTNERS_SUCCESS } from 'src/store/actions/partnerActions';
import { DISTRIBUTORS_SUCCESS } from 'src/store/actions/distributorActions';
import authService from 'src/services/authService';
import userService from 'src/services/userService';
import {
  selectAccount,
  selectActiveProvider,
  selectActivePartner,
  selectActiveDistributor,
  selectRolesByProviderId
} from 'src/store/selectors/accountSelectors';

function* fetchRoles() {
  try {
    const isAuthenticated = yield call(authService.isAuthenticated);
    if (isAuthenticated) {
      const accessToken = yield call(authService.getAccessToken);
      const { userId } = jwtDecode(accessToken);
      const { data } = yield call(userService.getUserById, userId);

      const { roles, partnerRoles, distributorRoles } = data;
      yield put({ type: RECEIVE_ROLES, payload: { roles, partnerRoles, distributorRoles } });
    } else {
      yield put({ type: FETCH_ROLES_FAILURE, payload: { error: new Error('User not authenticated.') } });
    }
  } catch (error) {
    yield put({ type: FETCH_ROLES_FAILURE, payload: { error } });
  }
}

function* fetchPermissions() {
  const { roles, user, partnerRoles, distributorRoles } = yield select(selectAccount);
  const provider = yield select(selectActiveProvider);
  const partner = yield select(selectActivePartner);
  const distributor = yield select(selectActiveDistributor);

  if (!user || !roles || (!provider && !partner && !distributor)) {
    return;
  }

  // Resolve the role from whichever active scope actually has one. We must NOT
  // lock onto the first active scope and bail: an external user can have a
  // lingering active provider/partner with no matching role while their real
  // role lives under distributorRoles. So we fall through scope by scope and
  // take the first that yields a role.
  let role;
  if (provider) {
    const rolesByProvider = yield select(selectRolesByProviderId);
    role = rolesByProvider?.role;
  }
  if (!role && partner) {
    role = partnerRoles?.[0]?.role;
  }
  if (!role && distributor) {
    role = distributorRoles?.[0]?.role;
  }

  if (!role) {
    return;
  }

  const { id } = role;
  const { permissions } = yield call(authService.getRolePermissions, id);
  yield put({
    type: RECEIVE_PERMISSIONS,
    payload: { permissions: permissions.map((item) => item.code) }
  });
}

export default function* root() {
  yield all([
    takeLatest(FETCH_ROLES, fetchRoles),
    takeLatest(
      [RECEIVE_ROLES, PROVIDERS_SUCCESS, PARTNERS_SUCCESS, DISTRIBUTORS_SUCCESS, FETCH_PERMISSIONS],
      fetchPermissions
    )
  ]);
}
